import { Cloud, Code } from '@mui/icons-material';
import {
  alpha,
  Avatar,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type { GetApiAccessResponse } from '@shared/types/api-access';
import type { ReactNode } from 'react';
import { Link } from '~/components/Link';
import { useFreeMonthlyCredits } from '~/hooks/useFreeMonthlyCredits';
import { useUser } from '~/hooks/useUser';
import { ApiCreditsCard } from '~/scenes/api/ApiCreditsCard';
import { ApiGuestCard } from '~/scenes/api/ApiGuestCard';
import { ApiKeyCard } from '~/scenes/api/ApiKeyCard';
import { ApiSignupCard } from '~/scenes/api/ApiSignupCard';
import { ApiUsageCard } from '~/scenes/api/ApiUsageCard';

const KEY_USES: Array<{
  icon: ReactNode;
  title: string;
  description: string;
  href: string;
}> = [
  {
    icon: <Code fontSize="small" />,
    title: 'REST API',
    description: 'Endpoints, parameters and examples',
    href: '/api#documentation',
  },
  {
    icon: <Cloud fontSize="small" />,
    title: 'MCP server',
    description: 'Connect Claude, Cursor and other LLMs',
    href: '/mcp',
  },
];

interface ApiKeyTabProps {
  apiAccess: GetApiAccessResponse | null | undefined;
  loading: boolean;
}
export const ApiKeyTab = ({ apiAccess, loading }: ApiKeyTabProps) => {
  const user = useUser();
  const freeMonthlyCredits = useFreeMonthlyCredits();
  if (user?.isGuest) {
    return <ApiGuestCard loading={loading} />;
  }
  if (!apiAccess?.api_key) {
    return loading ? <LoadingCard /> : null;
  }
  const monthlyCredits =
    apiAccess.credits?.monthly_credits ?? freeMonthlyCredits;
  return (
    <Grid container spacing={3}>
      {loading && (
        <Grid item xs={12}>
          <LoadingCard />
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <ApiKeyCard apiKey={apiAccess.api_key} />
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography variant="overline" color="text.secondary">
          Use your key with
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {KEY_USES.map(({ icon, title, description, href }) => {
            return (
              <Stack
                key={title}
                direction="row"
                spacing={2}
                alignItems="center">
                <Avatar
                  variant="rounded"
                  sx={(theme) => {
                    return {
                      width: 36,
                      height: 36,
                      color: 'primary.main',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    };
                  }}>
                  {icon}
                </Avatar>
                <div>
                  <Link
                    href={href}
                    preventScrollReset
                    underline="hover"
                    variant="subtitle1"
                    fontWeight={500}
                    display="block"
                    lineHeight={1.3}>
                    {title}
                  </Link>
                  <Typography variant="body2" color="text.secondary">
                    {description}
                  </Typography>
                </div>
              </Stack>
            );
          })}
        </Stack>
      </Grid>
      <Grid item xs={12} md={6}>
        <ApiCreditsCard credits={apiAccess.credits} loading={loading} />
      </Grid>
      <Grid item xs={12} md={6}>
        {apiAccess.pay_as_you_go ? (
          <Paper
            variant="outlined"
            sx={{ p: 4, height: '100%', bgcolor: 'grey.50' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}>
              <Typography variant="h5">Pay-as-you-go</Typography>
              <Chip label="Active" color="success" size="small" />
            </Stack>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Usage beyond your free monthly credits is billed at the end of
              each billing period.
            </Typography>
            <ApiUsageCard />
          </Paper>
        ) : (
          <ApiSignupCard loading={loading} monthlyCredits={monthlyCredits} />
        )}
      </Grid>
    </Grid>
  );
};

const LoadingCard = () => {
  return (
    <Card>
      <CardContent>
        <Typography>
          <CircularProgress size={20} sx={{ mr: 4 }} />
          Loading API Access...
        </Typography>
      </CardContent>
    </Card>
  );
};
